// src/services/azureDevOps.js
import axios from 'axios';
import authService from './authService';

class AzureDevOpsService {
    constructor() {
        console.log('Initializing AzureDevOpsService...');
        this.organization = process.env.REACT_APP_AZURE_DEVOPS_ORG;
        this.project = process.env.REACT_APP_AZURE_DEVOPS_PROJECT;
        this.baseUrl = `https://dev.azure.com/${this.organization}/${this.project}/_apis`;
        console.log('AzureDevOpsService initialized with:', {
            organization: this.organization,
            project: this.project,
            baseUrl: this.baseUrl
        });
    }

    async getHeaders() {
        try {
            const token = await authService.getToken();
            return {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            };
        } catch (error) {
            console.error('Error getting headers:', error);
            throw new Error('Failed to get authentication token');
        }
    }

    async getWorkItems(queryId) {
        try {
            const headers = await this.getHeaders();
            console.log('Getting work items for query:', queryId);

            if (!this.organization || !this.project) {
                throw new Error('Organization and project must be configured');
            }

            // First, get the query results
            const queryUrl = `${this.baseUrl}/wit/queries/${queryId}?$expand=wiql&api-version=7.0`;
            const queryResponse = await axios.get(queryUrl, { headers });

            if (!queryResponse.data.wiql) {
                throw new Error('Invalid query response - no WIQL found');
            }

            // Execute the WIQL query
            const wiqlUrl = `${this.baseUrl}/wit/wiql?api-version=7.0`;
            const wiqlResponse = await axios.post(wiqlUrl, {
                query: queryResponse.data.wiql
            }, { headers });

            if (!wiqlResponse.data.workItems?.length) {
                console.log('No work items found');
                return [];
            }

            // Get work items in batches of 200 (Azure DevOps recommended batch size)
            const workItemIds = wiqlResponse.data.workItems.map(item => item.id);
            const batchSize = 200;
            const batches = [];

            for (let i = 0; i < workItemIds.length; i += batchSize) {
                const batchIds = workItemIds.slice(i, i + batchSize);
                batches.push(batchIds);
            }

            console.log(`Processing ${batches.length} batches of work items`);

            // Process each batch
            const allWorkItems = [];
            for (const [index, batchIds] of batches.entries()) {
                console.log(`Processing batch ${index + 1} of ${batches.length}`);
                const workItemsUrl = `${this.baseUrl}/wit/workitems?ids=${batchIds.join(',')}&$expand=all&api-version=7.0`;

                try {
                    const workItemsResponse = await axios.get(workItemsUrl, { headers });
                    allWorkItems.push(...workItemsResponse.data.value);
                } catch (error) {
                    console.error(`Error processing batch ${index + 1}:`, error);
                    // Continue with other batches even if one fails
                }
            }

            console.log(`Successfully retrieved ${allWorkItems.length} work items`);
            return allWorkItems;

        } catch (error) {
            console.error('Error in getWorkItems:', error);
            if (error.response) {
                console.error('Response status:', error.response.status);
                console.error('Response data:', error.response.data);
            }
            throw error;
        }
    }

    async updateWorkItem(workItemId, updates) {
        try {
            const headers = await this.getHeaders();
            const url = `${this.baseUrl}/wit/workitems/${workItemId}?api-version=7.0`;

            // Format the updates into the required patch document format
            const patchDocument = updates.map(update => ({
                op: "add",
                path: `/fields/${update.field}`,
                value: update.value
            }));

            const response = await axios.patch(url, patchDocument, {
                headers: {
                    ...headers,
                    'Content-Type': 'application/json-patch+json'
                }
            });

            return response.data;
        } catch (error) {
            console.error('Error updating work item:', error);
            throw error;
        }
    }

    async getWorkItemComments(workItemId) {
        try {
            const headers = await this.getHeaders();
            const url = `${this.baseUrl}/wit/workitems/${workItemId}/comments?api-version=7.1-preview.3`;
            const response = await axios.get(url, { headers });
            return response.data.comments || [];
        } catch (error) {
            console.error('Error fetching comments:', error);
            throw error;
        }
    }

    async addComment(workItemId, text) {
        try {
            const headers = await this.getHeaders();
            const url = `${this.baseUrl}/wit/workitems/${workItemId}/comments?api-version=7.1-preview.3`;
            const response = await axios.post(url, { text }, { headers });
            return response.data;
        } catch (error) {
            console.error('Error adding comment:', error);
            throw error;
        }
    }

    async getWorkItemHistory(workItemId) {
        try {
            const headers = await this.getHeaders();
            const url = `${this.baseUrl}/wit/workitems/${workItemId}/updates?api-version=7.0`;
            const response = await axios.get(url, { headers });
            return response.data.value;
        } catch (error) {
            console.error('Error fetching work item history:', error);
            throw error;
        }
    }

    async getQueryById(queryId) {
        try {
            const headers = await this.getHeaders();
            const url = `${this.baseUrl}/wit/queries/${queryId}?$expand=all&api-version=7.0`;
            const response = await axios.get(url, { headers });
            return response.data;
        } catch (error) {
            console.error('Error fetching query:', error);
            throw error;
        }
    }

    async downloadWorkItemsCSV(queryId) {
        try {
            const workItems = await this.getWorkItems(queryId);
            const fields = process.env.REACT_APP_AVAILABLE_COLUMNS.split(',');

            // Create CSV content
            const csvRows = [];

            // Add headers
            csvRows.push(fields.map(field => `"${field.split('.').pop()}"`).join(','));

            // Add data rows
            workItems.forEach(item => {
                const row = fields.map(field => {
                    const value = item.fields[field];
                    if (value === null || value === undefined) return '""';
                    if (typeof value === 'object') {
                        return `"${value.displayName || JSON.stringify(value)}"`;
                    }
                    return `"${value.toString().replace(/"/g, '""')}"`;
                });
                csvRows.push(row.join(','));
            });

            // Create and download CSV file
            const csvContent = csvRows.join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = `work-items-${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error('Error downloading CSV:', error);
            throw error;
        }
    }
}

const azureDevOpsService = new AzureDevOpsService();
export default azureDevOpsService;